import React, { useEffect, useState, useRef } from 'react';  
import { useNavigate } from 'react-router-dom';  
import * as atlas from 'azure-maps-control';  
import area from '@turf/area';  
import "azure-maps-control/dist/atlas.min.css";  
import { locationData } from '../components/locationData';  
import { Oval } from 'react-loader-spinner';  
import { azureMapsApiKey, azureMapsScriptUrl, fetchGeojsonData } from './api'; // api.js からインポート  
  
const MapComponent = () => {  
  const navigate = useNavigate();  
  const [geojson, setGeojson] = useState(null);  
  const [centerCoordinates, setCenterCoordinates] = useState([0, 0]);  
  const [selectedFeature, setSelectedFeature] = useState(null);  
  const [featureArea, setFeatureArea] = useState(null);  
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);  
  const [selectedPrefecture, setSelectedPrefecture] = useState('北海道');  
  const [selectedCity, setSelectedCity] = useState('室蘭市');  
  const [number, setNumber] = useState('012050');  
  const [loading, setLoading] = useState(true);  
  const [noData, setNoData] = useState(false);  
  const abortControllerRef = useRef(null);  
  const cache = useRef({});  
  
  useEffect(() => {  
    abortControllerRef.current = new AbortController();  
  
    const fetchGeojson = async () => {  
      setLoading(true);  
      setNoData(false);  
      try {  
        const { geojson, centerCoordinates } = await fetchGeojsonData(number, abortControllerRef.current.signal, cache);  
        setGeojson(geojson);  
        setCenterCoordinates(centerCoordinates);  
      } catch (error) {  
        console.error('Error fetching or processing GeoJSON data:', error);  
        setNoData(true);  
      } finally {  
        setLoading(false);  
      }  
    };  
  
    fetchGeojson();  
  
    return () => {  
      abortControllerRef.current.abort();  
    };  
  }, [number]);  
  
  useEffect(() => {  
    const initializeMap = () => {  
      try {  
        if (!geojson || !centerCoordinates) throw new Error('GeoJSON data or center coordinates are not available');  
        const map = new atlas.Map('myMap', {  
          center: centerCoordinates,  
          zoom: 14,  
          style: 'satellite_road_labels',  
          authOptions: {  
            authType: 'subscriptionKey',  
            subscriptionKey: azureMapsApiKey  
          }  
        });  
        map.events.add('ready', () => {  
          const dataSource = new atlas.source.DataSource('dataSource');  
          map.sources.add(dataSource);  
          const polygonLayer = new atlas.layer.PolygonLayer(dataSource, null, {  
            fillColor: 'rgba(0, 255, 0, 0.9)',  
            strokeColor: 'green',  
            strokeWidth: 2  
          });  
          map.layers.add(polygonLayer);  
          dataSource.add(geojson);  
          map.events.add('click', polygonLayer, (e) => {  
            clearSelection();  
            if (e.shapes && e.shapes.length > 0) {  
              const properties = e.shapes[0].getProperties();  
              const polygon = e.shapes[0].toJson();  
              const areaValue = area(polygon);  
              setSelectedFeature(properties);  
              setFeatureArea(areaValue);  
              setIsDetailsVisible(true);  
            }  
          });  
          map.controls.add(new atlas.control.StyleControl({  
            mapStyles: ['satellite', 'satellite_road_labels', 'grayscale_dark', 'grayscale_light', 'night', 'road_shaded_relief', 'high_contrast_dark', 'high_contrast_light'],  
            layout: 'icons'  
          }), {  
            position: 'top-right'  
          });  
          map.controls.add([  
            new atlas.control.ZoomControl(),  
            new atlas.control.CompassControl(),  
            new atlas.control.PitchControl(),  
          ], {  
            position: "top-right"  
          });  
        });  
      } catch (error) {  
        console.error('Error initializing map:', error);  
      }  
    };  
  
    if (geojson && centerCoordinates) {  
      const script = document.createElement('script');  
      script.src = azureMapsScriptUrl; // api.js からインポートしたURLを使用  
      script.async = true;  
      script.onload = initializeMap;  
      document.body.appendChild(script);  
    }  
  }, [geojson, centerCoordinates]);  
  
  const clearSelection = () => {  
    setSelectedFeature(null);  
    setFeatureArea(null);  
    setIsDetailsVisible(false);  
  };  
  
  const toggleDetails = () => {  
    setIsDetailsVisible(!isDetailsVisible);  
    clearSelection();  
  };  
  
  const handleBack = () => {  
    try {  
      navigate(-1);  
    } catch (error) {  
      console.error('Error navigating back:', error);  
    }  
  };  
  
  const handlePrefectureChange = (e) => {  
    try {  
      const newPrefecture = e.target.value;  
      setSelectedPrefecture(newPrefecture);  
      const firstCity = locationData[newPrefecture][0];  
      setSelectedCity(firstCity.name);  
      setNumber(firstCity.code);  
    } catch (error) {  
      console.error('Error changing prefecture:', error);  
    }  
  };  
  
  const handleCityChange = (e) => {  
    try {  
      setSelectedCity(e.target.value);  
      const city = locationData[selectedPrefecture].find(city => city.name === e.target.value);  
      if (city) {  
        setNumber(city.code);  
      }  
    } catch (error) {  
      console.error('Error changing city:', error);  
    }  
  };  
  
  const styles = {  
    mapContainer: {  
      height: '100vh',  
      width: '100vw',  
      position: 'relative',  
    },  
    detailsPanel: {  
      position: 'absolute',  
      top: '60px',  
      left: '10px',  
      backgroundColor: 'rgba(240, 240, 240, 0.7)',  
      padding: '10px',  
      border: '1px solid #ccc',  
      textAlign: 'left',  
      width: 'calc(100% - 40px)',  
      maxWidth: '300px',  
      zIndex: 1000,  
      transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',  
      boxSizing: 'border-box',  
      transform: isDetailsVisible ? 'translateX(0)' : 'translateX(-110%)',  
      opacity: isDetailsVisible ? 1 : 0,  
    },  
    toggleButton: {  
      position: 'absolute',  
      top: '10px',  
      right: '10px',  
      padding: '5px 10px',  
      background: '#05723a',  
      color: 'white',  
      border: 'none',  
      cursor: 'pointer',  
    },  
    backButton: {  
      position: 'absolute',  
      top: '10px',  
      left: '10px',  
      padding: '5px 10px',  
      background: '#005a9e',  
      color: 'white',  
      border: 'none',  
      cursor: 'pointer',  
      zIndex: 1001,  
    },  
    selectContainer: {  
      display: 'flex',  
      alignItems: 'center',  
      position: 'absolute',  
      top: '10px',  
      left: '120px',  
      zIndex: 1001,  
    },  
    selectBox: {  
      marginLeft: '10px',  
      padding: '5px',  
      border: '1px solid #ccc',  
      borderRadius: '4px',  
    },  
    loaderOverlay: {  
      display: 'flex',  
      justifyContent: 'center',  
      alignItems: 'center',  
      position: 'absolute',  
      top: 0,  
      left: 0,  
      height: '100%',  
      width: '100%',  
      backgroundColor: 'rgba(255, 255, 255, 0.8)',  
      zIndex: 1500,  
      flexDirection: 'column',  
    },  
    loadingText: {  
      marginTop: '10px',  
      fontSize: '16px',  
      color: '#333',  
    },  
  };  
  
  return (  
    <div>  
      <button style={styles.backButton} onClick={handleBack}>前の画面に戻る</button>  
      <div style={styles.selectContainer}>  
        <label>  
          <select style={styles.selectBox} value={selectedPrefecture} onChange={handlePrefectureChange}>  
            {Object.keys(locationData).map(prefecture => (  
              <option key={prefecture} value={prefecture}>{prefecture}</option>  
            ))}  
          </select>  
        </label>  
        <label>  
          <select style={styles.selectBox} value={selectedCity} onChange={handleCityChange} disabled={!selectedPrefecture}>  
            {selectedPrefecture && locationData[selectedPrefecture].map(city => (  
              <option key={city.code} value={city.name}>{city.name}</option>  
            ))}  
          </select>  
        </label>  
      </div>  
      {loading && (  
        <div style={styles.loaderOverlay}>  
          <Oval  
            height={80}  
            width={80}  
            color="#4fa94d"  
            wrapperStyle={{}}  
            wrapperClass=""  
            visible={true}  
            ariaLabel='oval-loading'  
            secondaryColor="#4fa94d"  
            strokeWidth={2}  
            strokeWidthSecondary={2}  
          />  
          <div style={styles.loadingText}>農地データダウンロード中</div>  
        </div>  
      )}  
      {!loading && noData && (  
        <div style={{  
          textAlign: 'center',  
          marginTop: '20px',  
          fontSize: '18px',  
          color: 'red',  
          position: 'absolute',  
          top: '50%',  
          left: '50%',  
          transform: 'translate(-50%, -50%)',  
          zIndex: 2000,  
          backgroundColor: 'rgba(255, 255, 255, 0.8)',  
          padding: '20px',  
          borderRadius: '8px',  
        }}>  
          農地データなし  
        </div>  
      )}  
      <div id="myMap" style={styles.mapContainer}>  
        <div style={styles.detailsPanel}>  
          <button style={styles.toggleButton} onClick={toggleDetails}>  
            閉じる  
          </button>  
          <h2>詳細</h2>  
          {selectedFeature ? (  
            <div>  
              <p style={{ fontSize: '16px', fontWeight: 'bold' }}><strong>住所:</strong> {selectedFeature?.PinInfo?.[0]?.Address || 'N/A'}</p>  
              <p style={{ fontSize: '16px', fontWeight: 'bold' }}><strong>賃借:</strong> {selectedFeature?.PinInfo?.[0]?.RightClassification || 'N/A'}</p>  
              <p style={{ fontSize: '16px', fontWeight: 'bold' }}><strong>種別:</strong> {selectedFeature?.PinInfo?.[0]?.LandCategory || 'N/A'}</p>  
              <p style={{ fontSize: '16px', fontWeight: 'bold' }}><strong>面積:</strong> {featureArea ? `${(featureArea / 10000).toFixed(2)} ha` : 'N/A'}</p>  
            </div>  
          ) : (  
            <p style={{ fontSize: '14px', fontWeight: 'bold' }}>農地を選択してください</p>  
          )}  
        </div>  
      </div>  
    </div>  
  );  
};  
  
export default MapComponent;  
